import React, { useMemo } from 'react';
import {
  Box,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Autocomplete,
  Chip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';

const OPERATOR_LABELS = {
  eq: 'equals',
  neq: 'not equals',
  in: 'in',
  not_in: 'not in',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
  between: 'between',
  contains: 'contains',
  not_contains: 'does not contain',
  starts_with: 'starts with',
  exists: 'is set',
  not_exists: 'is not set',
};

const isUnaryOperator = (op) => op === 'exists' || op === 'not_exists';
const isListOperator = (op) => op === 'in' || op === 'not_in';
const isBetweenOperator = (op) => op === 'between';

export default function RuleConditionRow({
  condition,
  onChange,
  onRemove,
  fields,
  industries,
  industryGroups,
}) {
  const fieldsByCategory = useMemo(() => {
    const map = new Map();
    fields.forEach((f) => {
      if (!map.has(f.category)) map.set(f.category, []);
      map.get(f.category).push(f);
    });
    return map;
  }, [fields]);

  const descriptor = useMemo(
    () => fields.find((f) => f.path === condition.field),
    [fields, condition.field]
  );

  const setField = (path) => {
    const f = fields.find((x) => x.path === path);
    onChange({
      ...condition,
      field: path,
      operator: f?.allowedOperators?.[0] || 'eq',
      value: null,
      valueTo: null,
    });
  };

  const setOperator = (op) => {
    onChange({
      ...condition,
      operator: op,
      value: isUnaryOperator(op) ? null : condition.value,
      valueTo: isBetweenOperator(op) ? condition.valueTo : null,
    });
  };

  const setValue = (v) => onChange({ ...condition, value: v });
  const setValueTo = (v) => onChange({ ...condition, valueTo: v });

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      alignItems={{ md: 'center' }}
      sx={{ p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
    >
      <TextField
        select
        size="small"
        label="Field"
        value={condition.field || ''}
        onChange={(e) => setField(e.target.value)}
        sx={{ minWidth: 240 }}
      >
        {[...fieldsByCategory.entries()].flatMap(([cat, items]) => [
          <MenuItem key={`__${cat}`} disabled sx={{ fontWeight: 600, opacity: 0.7 }}>
            {cat}
          </MenuItem>,
          ...items.map((f) => (
            <MenuItem key={f.path} value={f.path} sx={{ pl: 3 }}>
              {f.label}
            </MenuItem>
          )),
        ])}
      </TextField>

      <TextField
        select
        size="small"
        label="Operator"
        value={condition.operator || ''}
        onChange={(e) => setOperator(e.target.value)}
        sx={{ minWidth: 160 }}
        disabled={!descriptor}
      >
        {(descriptor?.allowedOperators || []).map((op) => (
          <MenuItem key={op} value={op}>
            {OPERATOR_LABELS[op] || op}
          </MenuItem>
        ))}
      </TextField>

      <Box sx={{ flex: 1, minWidth: 240 }}>
        <ValueEditor
          descriptor={descriptor}
          operator={condition.operator}
          value={condition.value}
          valueTo={condition.valueTo}
          onValue={setValue}
          onValueTo={setValueTo}
          industries={industries}
          industryGroups={industryGroups}
        />
      </Box>

      <IconButton size="small" onClick={onRemove} title="Remove condition">
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

function ValueEditor({ descriptor, operator, value, valueTo, onValue, onValueTo, industries, industryGroups }) {
  if (!descriptor || isUnaryOperator(operator)) return null;

  const dataType = descriptor.dataType;
  const isList = isListOperator(operator);
  const isBetween = isBetweenOperator(operator);
  const isEnum = dataType === 'Enum';

  // industry.id / industryGroup.id with lookup
  if (descriptor.lookupSource === 'industries' || descriptor.lookupSource === 'industryGroups') {
    const opts = descriptor.lookupSource === 'industries' ? (industries || []) : (industryGroups || []);
    const label = descriptor.lookupSource === 'industries' ? 'Industry' : 'Industry group';
    return (
      <Autocomplete
        multiple={isList}
        size="small"
        options={opts}
        getOptionLabel={(o) => o?.name || ''}
        isOptionEqualToValue={(o, v) => o.id === (typeof v === 'object' ? v?.id : v)}
        value={
          isList
            ? (Array.isArray(value) ? opts.filter((i) => value.includes(i.id)) : [])
            : (opts.find((i) => i.id === value) || null)
        }
        onChange={(_, v) => onValue(isList ? (v || []).map((x) => x.id) : v?.id ?? null)}
        renderInput={(params) => <TextField {...params} label={label} size="small" />}
      />
    );
  }

  if (isEnum) {
    const options = descriptor.enumValues || [];
    return (
      <Autocomplete
        multiple={isList}
        size="small"
        options={options}
        value={isList ? (Array.isArray(value) ? value : []) : (value || null)}
        onChange={(_, v) => onValue(isList ? v || [] : v)}
        renderInput={(params) => <TextField {...params} label="Value" size="small" />}
        renderTags={(arr, getTagProps) =>
          arr.map((opt, idx) => <Chip key={opt} label={opt} size="small" {...getTagProps({ index: idx })} />)
        }
      />
    );
  }

  if (dataType === 'Bool') {
    return (
      <TextField
        select
        size="small"
        label="Value"
        value={value === true ? 'true' : value === false ? 'false' : ''}
        onChange={(e) => onValue(e.target.value === 'true')}
        sx={{ minWidth: 140 }}
      >
        <MenuItem value="true">True</MenuItem>
        <MenuItem value="false">False</MenuItem>
      </TextField>
    );
  }

  if (dataType === 'Int') {
    if (isBetween) {
      return (
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            type="number"
            label="From"
            value={value ?? ''}
            onChange={(e) => onValue(e.target.value === '' ? null : Number(e.target.value))}
          />
          <TextField
            size="small"
            type="number"
            label="To"
            value={valueTo ?? ''}
            onChange={(e) => onValueTo(e.target.value === '' ? null : Number(e.target.value))}
          />
        </Stack>
      );
    }
    if (isList) {
      return (
        <TextField
          size="small"
          label="Values (comma-separated)"
          value={Array.isArray(value) ? value.join(', ') : ''}
          onChange={(e) =>
            onValue(
              e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
                .map(Number)
                .filter((n) => !Number.isNaN(n))
            )
          }
          fullWidth
        />
      );
    }
    return (
      <TextField
        size="small"
        type="number"
        label="Value"
        value={value ?? ''}
        onChange={(e) => onValue(e.target.value === '' ? null : Number(e.target.value))}
        fullWidth
      />
    );
  }

  // String / CsvString
  if (isList) {
    return (
      <Autocomplete
        multiple
        freeSolo
        size="small"
        options={[]}
        value={Array.isArray(value) ? value : []}
        onChange={(_, v) => onValue(v)}
        renderInput={(params) => <TextField {...params} label="Values" size="small" />}
      />
    );
  }
  return (
    <TextField
      size="small"
      label="Value"
      value={value ?? ''}
      onChange={(e) => onValue(e.target.value)}
      fullWidth
    />
  );
}
